import { useOverlayState } from "@heroui/react";
import { Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { InputGroup, Modal, TextField } from "@/src/common/components/ui";
import { useAppSelector } from "@/src/common/redux/store";
import { getActiveCommunityId } from "@/src/community/store/community-selectors";
import { useDebouncedValue } from "../hooks/use-debounced-value";
import { useGlobalSearchQuery } from "../services/search-http.service";
import { SearchResultsList } from "./search-results-list";

const MIN_QUERY_LENGTH = 2;

interface GlobalSearchModalProps {
	isOpen: boolean;
	onClose: () => void;
}

export function GlobalSearchModal({ isOpen, onClose }: GlobalSearchModalProps) {
	const { t } = useTranslation("search");
	const communityId = useAppSelector(getActiveCommunityId);
	const [value, setValue] = useState("");
	const debounced = useDebouncedValue(value.trim(), 300);

	useEffect(() => {
		if (!isOpen) setValue("");
	}, [isOpen]);

	const shouldQuery = debounced.length >= MIN_QUERY_LENGTH && Boolean(communityId);
	const { data, isFetching } = useGlobalSearchQuery({ communityId: communityId ?? "", q: debounced, limit: 5 }, { skip: !shouldQuery });

	const state = useOverlayState({
		isOpen,
		onOpenChange: (open) => {
			if (!open) onClose();
		},
	});

	return (
		<Modal state={state}>
			<Modal.Backdrop>
				<Modal.Container>
					<Modal.Dialog className="w-full max-w-xl">
						<div className="border-b border-divider p-3">
							<TextField className="w-full" autoFocus aria-label={t("placeholder")}>
								<InputGroup>
									<InputGroup.Prefix>
										<Search size={18} />
									</InputGroup.Prefix>
									<InputGroup.Input autoFocus placeholder={t("placeholder")} value={value} onChange={(e) => setValue(e.target.value)} />
								</InputGroup>
							</TextField>
						</div>

						<div className="max-h-[60vh] overflow-y-auto">
							{value.trim().length < MIN_QUERY_LENGTH ? (
								<p className="px-4 py-8 text-center text-sm text-foreground/50">{t("min_chars")}</p>
							) : (
								<SearchResultsList results={data} isLoading={isFetching} onSelect={onClose} />
							)}
						</div>
					</Modal.Dialog>
				</Modal.Container>
			</Modal.Backdrop>
		</Modal>
	);
}
